import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { memoriesApi, AgentMemory } from '@/services/api/agentApi';
import * as errorUtils from '@/utils/errors';

interface MemoryFilters {
  type?: 'hypothesis' | 'fact' | 'directive' | 'correction';
  origin?: 'agent' | 'human' | 'web';
  aspects?: string[];
  minWeight?: number;
  community_id?: string;
  sortBy?: string;
  archived?: boolean;
  category?: string;
}

interface MemoriesStoreState {
  memories: AgentMemory[];
  total: number;
  filters: MemoryFilters;
  searchQuery: string;
  isLoading: boolean;
  error: string | null;
}

interface MemoriesStoreActions {
  fetchMemories: (params?: Record<string, string>) => Promise<void>;
  searchMemories: (query: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: MemoryFilters) => void;
}

export const useMemoriesStore = create<MemoriesStoreState & MemoriesStoreActions>()(
  immer((set, get) => ({
    memories: [],
    total: 0,
    filters: {},
    searchQuery: '',
    isLoading: false,
    error: null,

    fetchMemories: async (params) => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const filters = get().filters;
        const queryParams: Record<string, string> = { ...params };
        if (filters.type) queryParams.type = filters.type;
        if (filters.origin) queryParams.origin = filters.origin;
        if (filters.aspects?.length) queryParams.aspects = filters.aspects.join(',');
        if (filters.minWeight !== undefined) queryParams.minWeight = String(filters.minWeight);
        if (filters.community_id) queryParams.community_id = filters.community_id;
        if (filters.sortBy) queryParams.sortBy = filters.sortBy;
        if (filters.archived !== undefined) queryParams.archived = String(filters.archived);
        if (filters.category) queryParams.category = filters.category;

        const result = await memoriesApi.list(queryParams);
        set((s) => {
          s.memories = result.data;
          s.total = result.total || result.data.length;
          s.isLoading = false;
        });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessageOrFallback(error, 'Failed to fetch memories'); s.isLoading = false; });
      }
    },

    searchMemories: async (query: string) => {
      set((s) => { s.isLoading = true; s.error = null; s.searchQuery = query; });
      try {
        const filters = get().filters;
        const result = await memoriesApi.hybridSearch({
          query,
          aspects: filters.aspects,
          community_id: filters.community_id,
          limit: 50,
        });
        set((s) => {
          s.memories = result.data;
          s.total = result.total || result.data.length;
          s.isLoading = false;
        });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessageOrFallback(error, 'Search failed'); s.isLoading = false; });
      }
    },

    setSearchQuery: (query) => set((s) => { s.searchQuery = query; }),
    setFilters: (filters) => set((s) => { s.filters = filters; }),
  }))
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useMemoriesStore.setState({ memories: [], total: 0, isLoading: false, error: null, searchQuery: '', filters: {} });
        }
      }
    );
  });
}
